import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  FeatureGrid,
  FeatureRow,
  StatBand,
  CTASection,
  Reveal,
  Mock,
} from './kit.jsx'

const SMALL_FEATURES = [
  {
    icon: 'doc',
    title: 'One-click CSV export',
    body: 'Pull any filtered view — a region, an industry, a saved segment — into a clean CSV for your CRM, dialer, or spreadsheet in one click.',
  },
  {
    icon: 'layers',
    title: 'Dark & light themes',
    body: 'A full dark mode and light mode that follow your system or lock to your preference — easy on the eyes for reps who live in the tool all day.',
  },
  {
    icon: 'mail',
    title: 'Reply & activity notifications',
    body: 'Get notified the moment a prospect replies or a deal changes stage, so nothing sits cold in an inbox nobody is watching.',
  },
  {
    icon: 'receipt',
    title: 'Full events log',
    body: 'Every send, open, reply, stage change, and assignment is written to an auditable timeline on the account and across the workspace.',
  },
  {
    icon: 'lock',
    title: 'Region-scoped access',
    body: 'Scope reps to their assigned territories so they only ever see and work the accounts that belong to them.',
  },
  {
    icon: 'zap',
    title: 'Sub-second load',
    body: 'Paginated Lambda APIs over per-category tables mean any region opens in under a second — even across the full database.',
  },
]

const STATS = [
  { value: '10,942', label: 'Verified B2B leads' },
  { value: '13', label: 'Regions, ready on day one' },
  { value: '10', label: 'Data views, built in' },
  { value: '50', label: 'U.S. regions covered' },
]

export default function FeaturesPage() {
  return (
    <MarketingShell active="features">
      <Seo
        title="Features"
        description="A deep tour of Revailo Sales — a 10,942-company verified lead database, Apollo-style Gmail outreach sequences, a new-to-won pipeline, ten data views with an in-app assistant, and team collaboration."
        path="/features"
        keywords="b2b lead database, outreach sequences, sales pipeline, Gmail sequences, sales CRM, lead analytics"
      />

      <Section>
        <PageHero
          eyebrow="Features"
          title={
            <>
              The entire B2B sales motion, <br className="max-sm:hidden" />
              in one workspace.
            </>
          }
          lead="From sourcing the right companies in your market, to sending outreach from your own Gmail, to tracking every deal from new to won — Revailo Sales is built to run the whole cycle without the spreadsheets, list buys, and disconnected inboxes."
          primary={{ label: 'Start free', to: '/signup' }}
          secondary={{ label: 'Book a demo', to: '/demo' }}
        >
          <Reveal>
            <Mock kind="leads" />
          </Reveal>
        </PageHero>
      </Section>

      <Section id="leads">
        <Container>
          <div className="flex flex-col" style={{ gap: 96 }}>
            <FeatureRow
              eyebrow="The lead database"
              title="Every company in your market, already qualified"
              body="Revailo Sales ships with a full verified database — 10,942 companies — cleaned and structured on the real schema. Filter by region, industry, company size, and annual volume to narrow 11k records to the handful worth a call, then open any company to see its named contacts."
              bullets={[
                'Filter by region, industry, company size, and annual sales volume',
                'Rich firmographic fields — service area, established year, phone, and named contacts',
                '10,942 companies across the verticals you sell into, sortable and searchable',
              ]}
              media={
                <Reveal>
                  <Mock kind="leads" />
                </Reveal>
              }
            />
            <FeatureRow
              reverse
              eyebrow="Outreach sequences"
              title="Multi-step cadences that send from your own inbox"
              body="Build Apollo-style sequences — intro email, waits, follow-ups, a break-up — in a visual builder. Connect Gmail once and every message sends as you, from your real address. Replies come straight back to your inbox and get logged and tracked against the account automatically."
              bullets={[
                'Visual sequence builder with steps, waits, and branches',
                'Send and receive through your own Gmail — no shared blast domain',
                'Reply tracking that logs every response on the account timeline',
              ]}
              media={
                <Reveal>
                  <Mock kind="sequence" />
                </Reveal>
              }
            />
            <FeatureRow
              eyebrow="Deal flow & pipeline"
              title="From new to won, with the revenue signals attached"
              body="Deal Flow moves every company through a people-level funnel — new, contacted, quoted, won — with activity captured at each step. Revenue signals roll up automatically so you always know which accounts, regions, and campaigns are actually turning into jobs."
              bullets={[
                'Drag companies across a new-to-won pipeline',
                'Activity — sends, replies, stage changes — captured on every deal',
                'Revenue signals that show where the pipeline is really coming from',
              ]}
              media={
                <Reveal>
                  <Mock kind="dashboard" />
                </Reveal>
              }
            />
            <FeatureRow
              reverse
              eyebrow="Data & insights"
              title="Ten views on your market — plus an assistant that reads them"
              body="The Data workspace turns your activity into decisions: Overview, Opportunities, Regions, Accounts, Lead Quality, Revenue Signals, Campaigns, Forecast, Recommendations, and a Market Map. Ask the in-app assistant a question in plain English — 'top regions this quarter' — and it answers straight from your live workspace."
              bullets={[
                'Overview, Opportunities, Regions, Accounts, and Lead Quality at a glance',
                'Revenue Signals, Campaigns, Forecast, Recommendations, and a Market Map',
                'An in-app assistant that answers questions from your own data',
              ]}
              media={
                <Reveal>
                  <Mock kind="analytics" />
                </Reveal>
              }
            />
            <FeatureRow
              eyebrow="Collaboration"
              title="Put your whole team on the same source of truth"
              body="Invite reps with Cognito-backed sign-in, assign them regions, and give each a role that scopes what they can see and do. Managers watch the full workspace; reps focus on their territory — and every action lands on one shared, auditable timeline."
              bullets={[
                'Invite reps and assign them regions and trades',
                'Role-based access — owner, manager, and rep',
                'Secure Cognito authentication with a shared activity history',
              ]}
              media={
                <Reveal>
                  <Mock kind="inbox" />
                </Reveal>
              }
            />
          </div>
        </Container>
      </Section>

      <Section id="more" className="py-14">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="And the details that matter"
              title="The smaller capabilities that keep reps in the tool"
              lead="Export, theming, notifications, auditing, access control, and speed — the everyday things a team notices when they're working the database all day."
            />
          </Reveal>
          <div className="mt-14">
            <Reveal>
              <FeatureGrid items={SMALL_FEATURES} cols={3} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={STATS} />
          </Reveal>
        </Container>
      </Section>

      <CTASection
        title="See every feature working on your own market."
        body="Start free and load your region in seconds — the full database, sequences, and pipeline, no credit card and no data project."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Book a demo', to: '/demo' }}
      />
    </MarketingShell>
  )
}
